import { AnimatePresence, motion } from "framer-motion";
import { X, Minus, Plus } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { useCart } from "@/context/CartContext";
import { useAuth } from "@/context/AuthContext";
import { formatINR } from "@/data/products";
import { resolveImage } from "@/lib/api";

const EASE = [0.16, 1, 0.3, 1];

const CartDrawer = () => {
  const { detailed, total, setQty, open, setOpen } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();

  const close = () => setOpen(false);

  const goToCheckout = () => {
    close();
    if (user) navigate("/checkout");
    else navigate("/login?redirect=/checkout");
  };

  return (
    <AnimatePresence>
      {open && (
        <>
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            onClick={close}
            className="fixed inset-0 bg-[#111111]/40 backdrop-blur-sm z-50"
            data-testid="cart-backdrop"
          />
          <motion.aside
            initial={{ x: "100%" }}
            animate={{ x: 0 }}
            exit={{ x: "100%" }}
            transition={{ duration: 0.6, ease: EASE }}
            className="fixed top-0 right-0 bottom-0 w-full max-w-md bg-[#F9F8F6] z-50 flex flex-col border-l border-[#EBE8E3]"
            data-testid="cart-drawer"
          >
            <div className="flex items-center justify-between px-8 h-[72px] border-b border-[#EBE8E3]">
              <p className="text-[11px] tracking-[0.35em] uppercase text-[#5C5C5C]">Your Selection</p>
              <button onClick={close} data-testid="cart-close-btn" className="hover:rotate-90 transition-transform duration-500">
                <X size={18} strokeWidth={1.5} />
              </button>
            </div>

            <div className="flex-1 overflow-y-auto px-8 py-8 flex flex-col gap-8">
              {detailed.length === 0 && (
                <p className="font-serif-lux italic font-light text-3xl text-[#5C5C5C] mt-10" data-testid="cart-empty-msg">
                  Your vitrine is empty.
                </p>
              )}
              {detailed.map((item) => (
                <div key={item.key} className="flex gap-5" data-testid={`cart-item-${item.key}`}>
                  <img src={resolveImage(item.image)} alt={item.name} className="w-16 h-16 object-cover" />
                  <div className="flex-1">
                    <p className="font-serif-lux text-xl font-medium leading-tight">{item.name}</p>
                    <p className="text-[10px] tracking-[0.25em] uppercase text-[#5C5C5C] mt-1">{item.flavor}</p>
                    <div className="flex items-center gap-4 mt-3">
                      <button onClick={() => setQty(item.key, item.qty - 1)} data-testid={`cart-minus-${item.key}`} className="w-7 h-7 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300">
                        <Minus size={12} />
                      </button>
                      <span className="text-sm w-4 text-center" data-testid={`cart-qty-${item.key}`}>{item.qty}</span>
                      <button onClick={() => setQty(item.key, item.qty + 1)} data-testid={`cart-plus-${item.key}`} className="w-7 h-7 border border-[#EBE8E3] rounded-full flex items-center justify-center hover:border-[#C07C5F] transition-colors duration-300">
                        <Plus size={12} />
                      </button>
                    </div>
                  </div>
                  <p className="font-serif-lux text-lg">{formatINR(item.price * item.qty)}</p>
                </div>
              ))}
            </div>
            <div className="border-t border-[#EBE8E3] px-8 py-8">
              <div className="flex justify-between mb-6">
                <span className="text-[11px] tracking-[0.3em] uppercase text-[#5C5C5C]">Subtotal</span>
                <span className="font-serif-lux text-2xl font-medium" data-testid="cart-subtotal">{formatINR(total)}</span>
              </div>
              <button
                onClick={goToCheckout}
                disabled={detailed.length === 0}
                data-testid="checkout-start-btn"
                className="group w-full flex items-center justify-center gap-3 bg-[#111111] text-[#F9F8F6] rounded-full py-4 text-[11px] tracking-[0.3em] uppercase hover:bg-[#C07C5F] transition-colors duration-500 disabled:opacity-30 disabled:pointer-events-none"
              >
                Proceed to Checkout
              </button>
            </div>
          </motion.aside>
        </>
      )}
    </AnimatePresence>
  );
};

export default CartDrawer;
